<template>
  <Layout>
    <b-container fluid class="dash-wrap py-3">

      <!-- ================= PAGE HEADER ================= -->
      <b-row class="mb-4">
        <b-col cols="12">
          <div class="page-hero d-flex flex-column flex-md-row align-items-md-center justify-content-between">
            <div class="text-left">
              <div class="kicker">Collecte & vérification</div>
              <h3 class="mb-1 title">Documents d’identité</h3>
              <div class="subtitle">CIN & Passeports — recherche, aperçu et pagination</div>
            </div>

            <div class="mt-3 mt-md-0 d-flex align-items-center">
              <b-badge variant="light" class="pill mr-2" style="cursor: pointer;" @click="goCinCardSection()">
                CIN
              </b-badge>
              <b-badge variant="light" class="pill" style="cursor: pointer;"  @click="goPassportCardSection()">
                Passeports
              </b-badge>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- ================= CIN DOCUMENTS ================= -->
      <b-card id="cin-section" class="panel mb-4" body-class="p-0">
        <div class="panel-head d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center">
            <div class="panel-icon mr-3">
            </div>
            <div class="text-left">
              <div class="panel-title">Cartes d'identité nationale (CIN)</div>
              <div class="panel-subtitle">Recherchez par numéro CIN et consultez les résultats</div>
            </div>
          </div>
        </div>

        <div class="p-3 p-md-4">
          <b-row class="align-items-end text-left">
            <b-col md="5" lg="3" class="mb-3 mb-md-0">
              <label class="field-label mb-2">Nom & Prénom</label>
              <b-form-input
                v-model="nameCin"
                class="search-input"
                placeholder="Nom ou prénom"
                @keyup.enter="cinPageModel.page=1; getAllCin()"
              />
            </b-col>

            <b-col md="4" lg="3" class="mb-3 mb-md-0">
              <label class="field-label mb-2">N° CIN</label>
              <b-form-input
                v-model="cinNumber"
                class="search-input"
                placeholder="Ex: AB123456"
                @keyup.enter="cinPageModel.page=1; getAllCin()"
              />
            </b-col>

            <b-col md="4" lg="3" class="mb-3 mb-md-0">
              <label class="field-label mb-2">Type de carte d'identité</label>

                <CustomInput
                  type="select"
                  v-model="identityType"
                  :options="identityOptions"
                  label-key="text"
                  placeholder="Carte d'identité"
                  :reduce="item => item.value"
                />
            </b-col>


            <b-col md="3" lg="2" class="mb-3 mb-md-0">
              <b-button
                variant="primary"
                size="sm"
                class="btn-pro w-100"
                @click="cinPageModel.page=1; getAllCin()"
              >
                Rechercher
              </b-button>
            </b-col>
          </b-row>

          <div md="3" lg="5" class="text-right mt-2">
            <div class="meta">
              <span class="meta-dot"></span>
              <span>Page</span>
              <strong class="ml-1">{{ cinPageModel.page }}</strong>
              <span class="mx-2">•</span>
              <span>Total</span>
              <strong class="ml-1">{{ cinPageModel.total || 0 }}</strong>
            </div>
          </div>


          <hr class="soft-hr m-4" />

          <div v-if="cinListIsBusy" class="py-2">
            <b-row>
              <b-col v-for="n in 8" :key="'cin-skel-'+n" md="4" lg="3" class="mb-4">
                <div class="doc-card skel">
                  <div class="skel-img"></div>
                  <div class="skel-line w-70"></div>
                  <div class="skel-line w-90"></div>
                  <div class="skel-line w-80"></div>
                  <div class="skel-line w-60"></div>
                </div>
              </b-col>
            </b-row>
          </div>

          <b-row v-else>

            <b-col
              v-for="cin in cinList"
              :key="cin.id"
              md="4"
              lg="3"
              class="mb-4"
            >
              <div class="doc-card h-100">
                <div class="doc-top d-flex align-items-center justify-content-between">
                  <div class="doc-badge">
                    {{cin && cin.identityNature || 'CIN'}}
                  </div>
                  <div class="doc-mini">
                    {{ cin.nationalityCode }}
                  </div>
                </div>

                <div class="doc-media text-center img-wrapper">
                  <img
                    v-if="cin.identityNature === 'CIN_TUN' &&  cin.rectoName"
                    :src="'/api/image/get/'+ cin.rectoName"
                    alt="CIN Image"
                    class="doc-img"
                  />
                  <img
                    v-else-if="cin.identityNature === 'CIN_EURO' &&  cin.versoName"
                    :src="'/api/image/get/'+ cin.versoName"
                    alt="CIN Image"
                    class="doc-img"
                  />
                  <div v-else class="doc-fallback">
                  </div>
                </div>

                <div class="d-flex justify-content-between mt-2 mx-2">

                  <span :class="['status-pill', getOcrStatusClass(cin.ocrStatus)]">
                    <span class="status-dot"></span>
                    {{ getOcrStatusLabel(cin.ocrStatus) }}
                  </span>

                  <span :class="['status-pill', getOcrStatusClass(cin.syncStatus)]">
                    <span class="status-dot"></span>
                    {{ getSyncStatusLabel(cin.syncStatus) }}
                  </span>

                </div>


                <div class="doc-body">
                  <div class="doc-id text-center" style="min-height: 20px;" >{{ cin.nationalIdNumber }}</div>

                  <div class="doc-grid mt-3">
                    <div class="doc-item">
                      <div class="doc-label">Nom</div>
                      <div class="doc-value ellipsis">
                        {{ cin.lastName || '-' }}
                      </div>
                    </div>
                    <div class="doc-item">
                      <div class="doc-label">Prénom</div>
                      <div class="doc-value ellipsis">
                        {{ cin.firstName || '-' }}
                      </div>
                    </div>
                    <div class="doc-item">
                      <div class="doc-label">Naissance</div>
                      <div class="doc-value">{{ cin.birthDate || '-' }}</div>
                    </div>
                    <div class="doc-item">
                      <div class="doc-label">Lieu</div>
                      <div class="doc-value">{{ cin.birthPlace || '-' }}</div>
                    </div>
                  </div>
                </div>

                <div class="m-3">

                  <b-button
                    variant="primary"
                    size="sm"
                    class="btn-pro w-100 "
                    @click="goToDetailsPage(cin)"
                  >
                    Modifier
                  </b-button>

                </div>


              </div>
            </b-col>

            <b-col v-if="!cinList.length" cols="12">
              <div class="text-center py-5">
                <h5 class="empty-title mb-2">Aucun document CIN trouvé</h5>
                <p class="empty-subtitle text-muted mb-3">
                  Essayez un autre numéro ou effacez le filtre.
                </p>
                <b-button
                  variant="outline-primary"
                  class="btn-pro"
                  @click="cinNumber = null; cinPageModel.page = 1; getAllCin()"
                >
                  Réinitialiser la recherche
                </b-button>
              </div>
            </b-col>

          </b-row>

          <div class="pb-1 pt-2">
            <b-pagination
              v-if="cinPageModel.totalPages > 1"
              v-model="cinPageModel.page"
              :total-rows="cinPageModel.total"
              :per-page="cinPageModel.pageSize"
              @input="getAllCin"
              align="center"
              class="pagination-pro"
            />
          </div>
        </div>
      </b-card>

      <!-- ================= PASSPORT DOCUMENTS ================= -->
      <b-card id="passport-section" class="panel" body-class="p-0">
        <div class="panel-head d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center">
            <div class="panel-icon mr-3" style="background: linear-gradient(135deg, rgba(20,184,166,.18), rgba(59,130,246,.18));">
            </div>
            <div class="text-left">
              <div class="panel-title">Passeports</div>
              <div class="panel-subtitle">Recherchez par numéro de passeport</div>
            </div>
          </div>
        </div>

        <div class="p-3 p-md-4">
          <!-- Search bar -->
          <b-row class="align-items-end text-left">
            <!-- Name (First + Last) -->
            <b-col md="5" lg="3" class="mb-3 mb-md-0">
              <label class="field-label mb-2">Nom & Prénom</label>
              <b-form-input
                v-model="namePassport"
                class="search-input"
                placeholder="Nom ou prénom"
                @keyup.enter="passportPageModel.page=1; getAllPassport()"
              />
            </b-col>

            <!-- CIN -->
            <b-col md="4" lg="3" class="mb-3 mb-md-0">
              <label class="field-label mb-2">N° PASSPORT</label>
              <b-form-input
                v-model="passportNumber"
                class="search-input"
                placeholder="Ex: AB123456"
                @keyup.enter="passportPageModel.page=1; getAllPassport()"
              />
            </b-col>

            <!-- Button -->
            <b-col md="3" lg="2" class="mb-3 mb-md-0">
              <b-button
                variant="primary"
                size="sm"
                class="btn-pro w-100"
               @click="passportPageModel.page=1; getAllPassport()"
              >
                Rechercher
              </b-button>
            </b-col>
          </b-row>

            <div md="3" lg="5" class="text-right mt-2">
              <div class="meta">
                <span class="meta-dot"></span>
                <span>Page</span>
                <strong class="ml-1">{{ passportPageModel.page }}</strong>
                <span class="mx-2">•</span>
                <span>Total</span>
                <strong class="ml-1">{{ passportPageModel.total || 0 }}</strong>
              </div>
            </div>

          <hr class="soft-hr my-4" />

          <!-- Loader (skeleton) -->
          <div v-if="passportListIsBusy" class="py-2">
            <b-row>
              <b-col v-for="n in 8" :key="'pass-skel-'+n" md="4" lg="3" class="mb-4">
                <div class="doc-card skel">
                  <div class="skel-img"></div>
                  <div class="skel-line w-70"></div>
                  <div class="skel-line w-90"></div>
                  <div class="skel-line w-80"></div>
                  <div class="skel-line w-60"></div>
                </div>
              </b-col>
            </b-row>
          </div>

          <!-- Cards -->
          <b-row v-else>

            <b-col
              v-for="passport in passportList"
              :key="passport.id"
              md="4"
              lg="3"
              class="mb-4"
            >
              <div class="doc-card h-100">
                <div class="doc-top d-flex align-items-center justify-content-between">
                  <div class="doc-badge">
                    PASSEPORT
                  </div>
                  <div class="doc-mini">
                    {{ passport.nationalityCode }}
                  </div>
                </div>

                <div class="doc-media text-center img-wrapper">
                  <img
                    v-if="passport.pasName"
                    :src="'/api/image/get/'+ passport.pasName"
                    alt="PAS Image"
                    class="doc-img"
                  />
                  <div v-else class="doc-fallback">
                  </div>
                </div>

                <div class="d-flex justify-content-between mt-2 mx-2">

                  <span :class="['status-pill', getOcrStatusClass(passport.ocrStatus)]">
                    <span class="status-dot"></span>
                    {{ getOcrStatusLabel(passport.ocrStatus) }}
                  </span>

                  <span :class="['status-pill', getOcrStatusClass(passport.syncStatus)]">
                    <span class="status-dot"></span>
                    {{ getSyncStatusLabel(passport.syncStatus) }}
                  </span>

                </div>


                <div class="doc-body">
                  <div class="doc-id text-center"  style="min-height: 20px;" >{{ passport.nationalIdNumber }}</div>

                  <div class="doc-grid mt-3">
                    <div class="doc-item">
                      <div class="doc-label">Nom</div>
                      <div class="doc-value ellipsis">
                        {{ passport.lastName || '-' }}
                      </div>
                    </div>
                    <div class="doc-item">
                      <div class="doc-label">Prénom</div>
                      <div class="doc-value ellipsis">
                        {{ passport.firstName || '-' }}
                      </div>
                    </div>
                    <div class="doc-item">
                      <div class="doc-label">Naissance</div>
                      <div class="doc-value">{{ passport.birthDate || '-' }}</div>
                    </div>
                    <div class="doc-item">
                      <div class="doc-label">Expiration</div>
                      <div class="doc-value">{{ passport.expirationDate || '-' }}</div>
                    </div>
                  </div>
                </div>

                <div class="m-3">

                  <b-button
                    variant="primary"
                    size="sm"
                    class="btn-pro w-100 "
                    @click="goToDetailsPage(passport)"
                  >
                    Modifier
                  </b-button>

                </div>


              </div>
            </b-col>

           <b-col v-if="!passportList.length" cols="12">
            <div class="text-center py-5">
              <h5 class="empty-title mb-2">Aucun passeport trouvé</h5>
              <p class="empty-subtitle text-muted mb-3">
                Essayez un autre numéro ou effacez le filtre.
              </p>
              <b-button
                variant="outline-primary"
                class="btn-pro"
                @click="passportNumber = null; passportPageModel.page = 1; getAllPassport()"
              >
                Réinitialiser la recherche
              </b-button>
            </div>
          </b-col>

          </b-row>

          <!-- Pagination -->
          <div class="pb-1 pt-2">
            <b-pagination
              v-if="passportPageModel.totalPages > 1"
              v-model="passportPageModel.page"
              :total-rows="passportPageModel.total"
              :per-page="passportPageModel.pageSize"
              @input="getAllPassport"
              align="center"
              class="pagination-pro"
            />
          </div>
        </div>
      </b-card>

    </b-container>
  </Layout>
</template>

<script>
import Layout from "@/components/layout/LayoutScreen.vue";
import { getAllData } from '@/services/collecting-data-api.js';
import CustomInput from "@/components/common/customInput.vue";

export default {

  name: "DashboardScreen",
  components: {Layout, CustomInput},

  mounted(){
    this.getAllCin()
    this.getAllPassport()
  },

  data() {
    return {
      cinListIsBusy:false,
      passportListIsBusy:false,
      cinPageModel:{page:1, pageSize:12},
      passportPageModel:{page:1, pageSize:12},
      cinList:[],
      passportList:[],
      nameCin:null,
      cinNumber:null,
      namePassport:null,
      passportNumber:null,
      
      identityType: null,
      identityOptions: [
        { text: 'Carte d\'identité tunisienne', value: 0 },
        { text: 'Carte d\'identité européenne', value: 2 },
      ],
    };
  },

  watch: {
    identityType: function() {
      this.cinPageModel.page = 1;
      this.getAllCin();
    },
  },

  methods:{

      goToDetailsPage(document) {
        if (!document || !document.id) return

        this.$router.push({
          path: '/details',
          query: { id: document.id }
        })
      },

      goCinCardSection() {
        const el = document.getElementById('cin-section')
        if (el) {
          el.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          })
        }
      },

      goPassportCardSection() {
        const el = document.getElementById('passport-section')
        if (el) {
          el.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          })
        }
      },


      getOcrStatusLabel(code) {
        const map = {
          0: "Nouveau",
          1: "Extraitée",
          2: "Échec"
        }
        return map[code] ?? "-"
      },

      getSyncStatusLabel(code) {
        const map = {
          0: "Nouveau",
          1: "Synchronisé",
          2: "Échec"
        }
        return map[code] ?? "-"
      },

      getOcrStatusClass(code) {
        const map = {
          0: "status-new",
          1: "status-success",
          2: "status-failed"
        }
        return map[code] ?? "status-default"
      },



    // START API

    async getAllCin() {

      this.cinListIsBusy= true
      try{
        const params = {
          page: this.cinPageModel.page,
          pageSize: this.cinPageModel.pageSize,
          nationalIdNumber: this.cinNumber,
          name: this.nameCin,
          status: this.status,
          type:this.identityType, //CIN
        };

        const result = await getAllData(params);
        this.cinList = result.content || [];
        this.cinPageModel.total = result.totalElements;
        this.cinPageModel.totalPages = result.totalPages;
        this.cinPageModel.page = result.pageNumber;
      } finally{
         this.cinListIsBusy= false
      }
    },

    async getAllPassport() {
      this.passportListIsBusy= true
      try{
        const params = {
          page: this.passportPageModel.page,
          pageSize: this.passportPageModel.pageSize,
          nationalIdNumber: this.passportNumber,
          name: this.namePassport,
          status: this.status,
          type:1, //PASS
        };
        const result = await getAllData(params);
        this.passportList = result.content || [];
        this.passportPageModel.total = result.totalElements;
        this.passportPageModel.totalPages = result.totalPages;
        this.passportPageModel.page = result.pageNumber;
      } finally{
         this.passportListIsBusy= false
      }
    },
  }
};
</script>


<style scoped>
.dash-wrap {
  max-width: 80%;
  min-height: calc(100vh - 60px);
}

@media (max-width: 640px) {
  .dash-wrap {
    max-width: 90%;
  }
}

.doc-grid-wrapper {
  overflow-x: auto;       /* enable horizontal scroll */
  -webkit-overflow-scrolling: touch; /* smooth scroll on mobile */
}

.page-hero{
  border-radius: 18px;
  padding: 18px 18px;
  background: linear-gradient(135deg, #e1dac8, #e1dac845);
  border: 1px solid rgba(15, 23, 42, .06);
  box-shadow: 0 12px 35px rgba(2, 6, 23, .06);
}
.kicker{
  font-size: .78rem;
  letter-spacing: .06em;
  text-transform: uppercase;
  color: rgba(15, 23, 42, .55);
}
.title{
  font-weight: 800;
  color: #0f172a;
}
.subtitle{
  color: rgba(15, 23, 42, .65);
}
.pill{
  border-radius: 999px;
  padding: .5rem .75rem;
  border: 1px solid rgba(15, 23, 42, .08);
  color: rgba(15, 23, 42, .75);
  background: rgba(255,255,255,.55);
}

.panel{
  border: 1px solid rgba(15, 23, 42, .07);
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 14px 40px rgba(2, 6, 23, .06);
}
.panel-head{
  padding: 16px 18px;
  background: linear-gradient(180deg, #e1dac845, rgba(255,255,255,.70));
  border-bottom: 1px solid rgba(15, 23, 42, .06);
}
.panel-icon{
  width: 44px;
  height: 44px;
  display:flex;
  align-items:center;
  justify-content:center;
  border-radius: 14px;
  background: linear-gradient(135deg,  #e1dac8, #e1dac845);
  border: 1px solid rgba(15, 23, 42, .06);
  color: rgba(15, 23, 42, .78);
}
.panel-title{
  font-weight: 800;
  color: #0f172a;
}
.panel-subtitle{
  font-size: .9rem;
  color: rgba(15, 23, 42, .60);
}
.soft-hr{
  border-top: 1px solid rgba(15, 23, 42, .08);
}

.field-label{
  font-size: .85rem;
  font-weight: 700;
  color: rgba(15, 23, 42, .7);
}
.search-wrap{
  position: relative;
}
.search-ico{
  position: absolute;
  top: 50%;
  left: 12px;
  transform: translateY(-50%);
  color: rgba(15, 23, 42, .45);
  font-size: .95rem;
}
.search-input{
  border-radius: 5px;
  border: 1px solid rgba(15, 23, 42, .12);
  box-shadow: 0 10px 20px rgba(2, 6, 23, .05);
}
.search-input:focus{
  border-color: rgba(59,130,246,.45);
  box-shadow: 0 0 0 .2rem rgba(59,130,246,.12);
}
.clear-btn{
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  color: rgba(15, 23, 42, .55);
  text-decoration: none !important;
  padding: 0.25rem .5rem;
  border-radius: 10px;
}
.clear-btn:hover{
  background: rgba(15,23,42,.06);
}

.btn-pro{
  border-radius: 14px;
  height: 44px;
  font-weight: 700;
  box-shadow: 0 12px 24px rgba(59,130,246,.18);
}

.meta{
  font-size: .92rem;
  color: rgba(15, 23, 42, .65);
  display: inline-flex;
  align-items: center;
}
.meta-dot{
  width: 8px;
  height: 8px;
  border-radius: 999px;
  margin-right: 8px;
  background: rgba(59,130,246,.65);
}

.doc-card{
  border-radius: 18px;
  background: rgba(255,255,255,.88);
  border: 1px solid rgba(15, 23, 42, .07);
  box-shadow: 0 14px 30px rgba(2, 6, 23, .06);
  overflow: hidden;
  transition: transform .18s ease, box-shadow .18s ease;
}
.doc-card:hover{
  transform: translateY(-6px);
  box-shadow: 0 22px 45px rgba(2, 6, 23, .10);
}
.doc-top{
  padding: 12px 12px 0 12px;
}
.doc-badge{
  font-size: .72rem;
  font-weight: 800;
  letter-spacing: .06em;
  padding: .35rem .6rem;
  border-radius: 999px;
  background: rgba(59,130,246,.10);
  border: 1px solid rgba(59,130,246,.20);
  color: rgba(15, 23, 42, .80);
}
.doc-badge.alt{
  background: rgba(16,185,129,.10);
  border: 1px solid rgba(16,185,129,.22);
}
.doc-mini{
  font-size: .78rem;
  color: rgba(15, 23, 42, .55);
}

.doc-media{
  padding: 12px 12px 0 12px;
}

.img-wrapper {
  position: relative;
  display: inline-block;
}

.doc-img {
  width: 100%;
  height: 135px;
  object-fit: cover;
  border-radius: 14px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  transition: all 0.3s ease;
}

.doc-img:active{
    height: auto;
}

.doc-fallback{
  height: 135px;
  border-radius: 14px;
  border: 1px dashed rgba(15, 23, 42, .18);
  background: linear-gradient(135deg, rgba(59,130,246,.08), rgba(99,102,241,.06));
  display:flex;
  align-items:center;
  justify-content:center;
  color: rgba(15, 23, 42, .45);
  font-size: 2.2rem;
}
.doc-fallback.alt{
  background: linear-gradient(135deg, rgba(16,185,129,.08), rgba(59,130,246,.06));
}

.doc-body{
  padding: 10px;
}
.doc-id{
  font-weight: 900;
  color: #0f172a;
  letter-spacing: 2px;
}
.doc-grid{
  display:grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 12px;
}
.doc-item{
  background: rgba(15,23,42,.03);
  border: 1px solid rgba(15,23,42,.06);
  border-radius: 14px;
  padding: 10px 10px;
  flex: 0 0 auto;
  white-space: nowrap; 
}
.doc-label{
  font-size: .72rem;
  letter-spacing: .06em;
  text-transform: uppercase;
  color: rgba(15, 23, 42, .55);
  font-weight: 800;
  margin-bottom: 2px;
}
.doc-value{
  font-weight: 700;
  color: rgba(15, 23, 42, .85);
  font-size: .92rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.empty-state{
  border-radius: 18px;
  border: 1px dashed rgba(15,23,42,.18);
  background: rgba(255,255,255,.72);
  padding: 28px 18px;
  text-align: center;
}
.empty-ico{
  width: 54px;
  height: 54px;
  border-radius: 18px;
  margin: 0 auto 12px auto;
  background: rgba(59,130,246,.10);
  border: 1px solid rgba(59,130,246,.20);
  display:flex;
  align-items:center;
  justify-content:center;
  color: rgba(15,23,42,.65);
  font-size: 1.6rem;
}
.empty-title{
  font-weight: 900;
  color: #0f172a;
}
.empty-subtitle{
  color: rgba(15, 23, 42, .62);
}


.pagination-pro ::v-deep .page-link{
  border-radius: 12px !important;
  margin: 0 4px;
  border: 1px solid rgba(15,23,42,.12);
}
.pagination-pro ::v-deep .page-item.active .page-link{
  box-shadow: 0 12px 24px rgba(59,130,246,.18);
}

.skel{
  padding: 12px;
}
.skel-img{
  height: 135px;
  border-radius: 14px;
  background: linear-gradient(90deg, rgba(15,23,42,.06), rgba(15,23,42,.03), rgba(15,23,42,.06));
  animation: shimmer 1.1s infinite linear;
}
.skel-line{
  height: 10px;
  border-radius: 999px;
  margin-top: 12px;
  background: linear-gradient(90deg, rgba(15,23,42,.06), rgba(15,23,42,.03), rgba(15,23,42,.06));
  animation: shimmer 1.1s infinite linear;
}
.w-70{ width: 70%; }
.w-90{ width: 90%; }
.w-80{ width: 80%; }
.w-60{ width: 60%; }

@keyframes shimmer{
  0% { background-position: 0% 0%; }
  100% { background-position: 200% 0%; }
}
</style>



<style>

.doc-grid-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: rgba(0,0,0,0.2) transparent;
}

.doc-grid-wrapper::-webkit-scrollbar {
  height: 6px;
}

.doc-grid-wrapper::-webkit-scrollbar-track {
  background: transparent;
}

.doc-grid-wrapper::-webkit-scrollbar-thumb {
  background-color: rgba(0,0,0,0.2);
  border-radius: 3px;
}

.doc-grid-wrapper::-webkit-scrollbar-thumb:hover {
  background-color: rgba(0,0,0,0.4);
}


.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
  transition: all 0.2s ease;
}

/* NEW */
.status-new {
  background: rgba(59, 130, 246, 0.08);
  color: #3b82f6;
}

/* SUCCESS */
.status-success {
  background: rgba(16, 185, 129, 0.08);
  color: #10b981;
}

/* FAILED */
.status-failed {
  background: rgba(239, 68, 68, 0.08);
  color: #ef4444;
}

/* Default */
.status-default {
  background: #f3f4f6;
  color: #6b7280;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}


.ellipsis {
  max-width: 130px;       /* adjust width as needed */
  white-space: nowrap;     /* prevents wrapping to new line */
  overflow: hidden;        /* hides overflowing text */
  text-overflow: ellipsis; /* adds the "…" */
}

</style>